import React from "react";
import analytics from "web/script/analytics/analytics";
import LazyImage from "web/react/components/lazy-load-image/lazy-load-image";
import { useProductCardContext } from "./product-card";
import ProductCardLink from "./product-card-link";
import styles from "./product-card-image.css";

interface ProductCardImageProps {
    width: number;
    height: number;
    children?: React.ReactNode;
    lazyLoad?: boolean;
}

function ProductCardImageWrapper({
    width,
    height,
    children,
    lazyLoad = true,
}: ProductCardImageProps): React.ReactElement {
    return (
        <div className={styles.imageWrapper}>
            {children}

            <ProductCardImage width={width} height={height} lazyLoad={lazyLoad} />
        </div>
    );
}

export function ProductCardImage({
    width,
    height,
    lazyLoad = true,
}: ProductCardImageProps): React.ReactElement {
    const {
        is_affiliate: isAffiliate,
        lead_enabled: leadEnabled,
        use_lazy_images: useLazyImages,
        image_url: imageURL,
        image_alt_text: imageAltText,
    } = useProductCardContext();

    function handleClick(): void {
        analytics.event("product_card", "image");
    }

    const shouldRenderLeadLinkInImage = isAffiliate && leadEnabled;

    if (!imageURL) {
        return (
            <div style={{ position: "relative", width, height }}>
                <ProductCardLink
                    className={styles.imageLink}
                    reason="image"
                    predicate={shouldRenderLeadLinkInImage}
                    onClick={handleClick}
                />
            </div>
        );
    }

    return (
        <ProductCardLink
            reason="image"
            predicate={shouldRenderLeadLinkInImage}
            onClick={handleClick}
        >
            {useLazyImages && lazyLoad ? (
                <LazyImage
                    src={imageURL}
                    alt={imageAltText}
                    width={width}
                    height={height}
                    placeholder={`data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}"3E%3C/svg%3E`}
                    className={styles.image}
                    data-pin-no-hover="true"
                />
            ) : (
                <img
                    width={width}
                    height={height}
                    src={imageURL}
                    alt={imageAltText}
                    className={styles.image}
                    data-pin-no-hover="true"
                />
            )}
        </ProductCardLink>
    );
}

export default ProductCardImageWrapper;
